import * as React from 'react';
import {useParams} from 'react-router';
import TabPanel from '@mui/lab/TabPanel';
import TabContext from '@mui/lab/TabContext';
import {Grid} from '@mui/material';
import {useCallback, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import JobProofs from '../../../componentsOld/jobs/SingleJob/JobProofs/JobProofs';
import {JobPurchaseOrders} from '../../../componentsOld/jobs/SingleJob/JobPurchaseOrders';
import {JobShipping} from '../../../componentsOld/jobs/SingleJob/JobShipping';
import JobTasks from '../../../componentsOld/jobs/SingleJob/JobTasks';
import Files from '../../../modules/file/Files';
import {permissionCheck, SubscriptionTypes} from '../../../componentsHoops/AccessControl';
import {CommentsEntityViewer} from '../../../componentsHoops/Comments';
import {useGetJob, useUpdateJob} from '../../../hooks/api';
import {getJob} from '../../../actions/jobs';
import {HoopsTabs, JobHeading, JobStatusBar} from '../Components';
import {JobActivityTab, JobCredits, JobDetails} from '../Components/tabs';

export const JobPage = ({job: initialJobFromJobBoard = null, updateJobBoard}) => {
  const dispatch = useDispatch();
  const {_id: jobId} = useParams();
  const {data: jobFull, refetch} = useGetJob(initialJobFromJobBoard?._id || jobId, {});
  const jobSelector = (state) => state.jobsReducer.activeJob;
  const activeJob = useSelector(jobSelector);

  // When mounted, the job is passed from job board, but it does not have all of the data in the form expected by
  // the old job page. The old job page can also be loaded by other means so we can't just use the new form.
  // When mounted we will use the new format initially, while the full job is loaded using the old API. Once the
  // new job is loaded we will use it.
  const job = useMemo(() => ({
    ...initialJobFromJobBoard,
    ...activeJob,
    voided: jobFull?.voided,
    completed: jobFull?.completed,
    departmentalStatuses: jobFull?.departmentalStatuses ?? initialJobFromJobBoard?.departmentalStatuses,
    viewFields: jobFull?.viewFields ?? initialJobFromJobBoard?.viewFields
  }), [initialJobFromJobBoard, activeJob, jobFull]);

  const {mutateAsync: updateJob} = useUpdateJob();
  const [selectedTab, setSelectedTab] = React.useState('details');
  const companySelector = (state) => state.companyReducer.company;
  const company = useSelector(companySelector);

  useEffect(() => {
    // This condition runs when loaded from the JobBoard
    if (initialJobFromJobBoard?._id) {
      dispatch(getJob(initialJobFromJobBoard?._id));
    }

    // When this page isn't loaded from the JobBoard load the job
    if (jobId && !initialJobFromJobBoard?._id) {
      dispatch(getJob(jobId));
    }
  }, [initialJobFromJobBoard?._id, jobId, dispatch, job.viewFields, job.departmentalStatuses]);

  const handleTabChange = (e, tab) => {
    setSelectedTab(tab);
  };

  const refetchJob = useCallback(async () => {
    // When status or archived has been changed getJob retrieves the job with the updated data
    dispatch(getJob(initialJobFromJobBoard?._id || jobId));
    await refetch();
    await updateJobBoard();
  }, [initialJobFromJobBoard, dispatch, jobId, refetch, updateJobBoard]);

  const onStatusSelect = useCallback(async (selected, field) => {
    await updateJob({
      id: job._id,
      data: {[field]: selected.statusLabel}
    });
    await refetchJob();
  }, [job, updateJob, refetchJob]);

  const jobIds = useMemo(() => [job._id], [job._id]);
  const jobs = useMemo(() => [job], [job]);

  return (
    <TabContext value={selectedTab}>
      <Grid container padding={3}>
        <Grid item xs={12}>
          <JobHeading data-intercom-target={'job-heading'} job={job} updateJobBoard={refetchJob} company={company} />
        </Grid>
        <JobStatusBar job={job} onSelect={onStatusSelect} />
        <Grid item xs={12}>
          <HoopsTabs
            value={selectedTab}
            onChange={handleTabChange}
            items={
              [
                {label: 'Job Detail', value: 'details'},
                ...permissionCheck({disallowedSubscriptions: [SubscriptionTypes.proofs]}) ? [{label: 'Purchase Orders', value: 'purchase-orders'}] : [],
                {label: 'Proofs', value: 'proofs'},
                ...permissionCheck({disallowedSubscriptions: [SubscriptionTypes.proofs]}) ? [{label: 'Files', value: 'files'}] : [],
                {label: 'Comments', value: 'comments'},
                ...permissionCheck({disallowedSubscriptions: [SubscriptionTypes.proofs]}) ? [{label: 'Tasks', value: 'tasks'}] : [],
                {label: 'Activity', value: 'activity'},
                ...permissionCheck({disallowedSubscriptions: [SubscriptionTypes.proofs]}) ? [{label: 'Shipping', value: 'shipping'}] : [],
                ...permissionCheck({allowedSubscriptions: [SubscriptionTypes.fullyPromoted]}) ? [{label: 'Credits', value: 'credits'}] : [],
              ]
            }
          />
        </Grid>
        <Grid item xs={12}>
          <TabPanel value='details' sx={{p: 0, pt: 3}}><JobDetails job={job} updateJobBoard={updateJobBoard} /></TabPanel>
          <TabPanel value='purchase-orders' sx={{p: 0, pt: 3}}><JobPurchaseOrders job={job} /></TabPanel>
          <TabPanel value='proofs' sx={{p: 0, pt: 3}}><JobProofs updateJobBoard={updateJobBoard} /></TabPanel>
          <TabPanel value='files' sx={{p: 0, pt: 3}}><Files
            customerId={job?.customerId}
            getEntityIds={jobIds}
            setEntityIds={jobIds}
            isProof={false}
            isWorkable={true}
            createTaskState={{jobIds, jobs}}
            showCustomerBtn={true}
            showUploadFileBtn={true}
            entityTitle='Job'
          /></TabPanel>
          <TabPanel value='comments' sx={{p: 0, pt: 3}}>
            <CommentsEntityViewer entity={'job'} entityId={job._id} entityNumber={job.number} />
          </TabPanel>
          <TabPanel value='tasks' sx={{p: 0, pt: 3}}><JobTasks job={job} /></TabPanel>
          <TabPanel value='activity' sx={{p: 0, pt: 3}}><JobActivityTab job={job} /></TabPanel>
          <TabPanel value='shipping' sx={{p: 0, pt: 3}}><JobShipping job={job} /></TabPanel>
          <TabPanel value='credits' sx={{p: 0, pt: 3}}><JobCredits job={job} /></TabPanel>
        </Grid>
      </Grid>
    </TabContext>

  );
};
